import {
  GRAHA_NAMES,
  WESTERN_PLANETS,
  type AspectHit,
  type ChartResult,
  type GocharPlacement,
  type Planet,
  type TransitHit,
} from '@astrally/shared';
import { formatDegMin } from '@astrally/astro-engine';
import { Term } from '../../components/Term';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* The old JyotishPlanetTable lived here; GrahaDetail.tsx replaced it — same
   data, one graha per row with tappable terms instead of a grid of columns. */

/** Indian graha name when the body is one of the nine; capitalised id otherwise. */
const INDIAN_NAME: Partial<Record<Planet, string>> = GRAHA_NAMES;
const grahaName = (p: Planet) => INDIAN_NAME[p] ?? cap(p);

export function WesternPlanetTable({ chart }: { chart: ChartResult }) {
  const positions = chart.positions ?? [];
  if (positions.length === 0) return null;
  const rows = WESTERN_PLANETS.map((pl) => positions.find((p) => p.planet === pl)).filter(
    (p): p is NonNullable<typeof p> => !!p,
  );
  const houses = chart.western?.housePlacements;

  return (
    <div className="card" style={{ overflowX: 'auto', padding: 10 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Planet</th>
            <th>Sign</th>
            <th>Deg</th>
            {houses && <th>House</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.planet}>
              <td>
                {cap(p.planet)}
                {p.retrograde && <span className="gold"> ℞</span>}
              </td>
              <td>{p.tropical.signName}</td>
              <td>{formatDegMin(p.tropical.degInSign)}</td>
              {houses && <td>{houses[p.planet] ?? '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AspectTable({ aspects }: { aspects: AspectHit[] }) {
  if (aspects.length === 0) return null;
  return (
    <div className="card" style={{ overflowX: 'auto', padding: 10 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Point</th>
            <th>Aspect</th>
            <th>Point</th>
            <th>Orb</th>
          </tr>
        </thead>
        <tbody>
          {aspects.map((a, i) => (
            <tr key={i}>
              <td>{cap(String(a.a))}</td>
              <td className="accent">{a.type}</td>
              <td>{cap(String(a.b))}</td>
              <td className="muted">{a.orb.toFixed(1)}°</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TransitPanel({
  gochar,
  transits,
}: {
  gochar: GocharPlacement[];
  transits: TransitHit[];
}) {
  return (
    <div className="col">
      <div className="card" style={{ overflowX: 'auto', padding: 10 }}>
        <div className="h3" style={{ marginBottom: 6 }}>
          <Term id="gochara">Gochar</Term> — where the grahas are today
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Graha</th>
              <th>Rashi</th>
              <th>Nakshatra</th>
              <th>From Moon</th>
            </tr>
          </thead>
          <tbody>
            {gochar.map((g) => (
              <tr key={g.planet}>
                <td>
                  {grahaName(g.planet)}
                  {g.retrograde && <span className="gold"> ℞</span>}
                </td>
                <td>{g.sidereal.signName}</td>
                <td>{g.nakshatra.name}</td>
                <td>{g.houseFromMoon ? `bhava ${g.houseFromMoon}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {transits.length > 0 && (
        <div className="card">
          <div className="h3" style={{ marginBottom: 6 }}>Western transits to natal points</div>
          <div className="col" style={{ gap: 4 }}>
            {transits.slice(0, 12).map((t, i) => (
              <div key={i} className="small muted">
                {cap(t.transiting)} <span className="accent">{t.type}</span> natal{' '}
                {cap(String(t.natal))} <span className="faint">({t.orb.toFixed(1)}°)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
